'use client';

import { useState } from 'react';
import { Box, VStack, HStack } from '@terra/ui-styled-system';
import { Text } from '../../components/styled/text';
import { Spinner } from '../../components/spinner';
import {
  AlertCircleIcon,
  ArrowDown01Icon,
  ArrowRight01Icon,
} from '@terra/ui-icons';
import type { ToolExecutionHar } from '@terra/shared-contracts-tracker';
import { useTranslations } from 'next-intl';
import { useChatHar } from '../chat/har/ChatHarProvider';
import { ToolExecutionHarRequestResponseEditors } from '../har/ToolExecutionHarRequestResponseEditors';
import { useHarById } from './useHarById';
import { renderHttpBadge } from '../../utils/http-badge.utils';
import { getUrlPath } from '../../utils/url-path.utils';
import { ToolHarCredentialBadge } from '../chat/components/ToolHarCredentialBadge';

export type EvidenceHarExchangeRowProps = {
  toolExecutionHarId: string;
  harData?: ToolExecutionHar | null;
  defaultExpanded?: boolean;
  hopLabel?: string;
};
export function EvidenceHarExchangeRow({
  toolExecutionHarId,
  harData,
  defaultExpanded = false,
  hopLabel,
}: EvidenceHarExchangeRowProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const tArtifacts = useTranslations('designSystem.artifacts');
  const { fetchHarById } = useChatHar();
  const shouldFetchHar = harData === undefined;
  const {
    data: fetchedHar,
    isLoading,
    error,
  } = useHarById({
    toolExecutionHarId,
    fetchHarById,
    enabled: shouldFetchHar && !!toolExecutionHarId,
  });
  const har = shouldFetchHar ? fetchedHar : harData;
  const harEntry = har?.harEntry;
  const isMissingHarEntry = !!har && !harEntry;
  const errorMessage =
    (shouldFetchHar && error) || isMissingHarEntry
      ? tArtifacts('failedToLoadEvidence')
      : null;

  return (
    <VStack alignItems="stretch" gap={0} width="100%">
      {isLoading && (
        <HStack justify="center" p={4} gap={2}>
          <Spinner size="sm" />
          <Text color="fg.muted" fontSize="sm">
            {tArtifacts('loadingEvidence')}
          </Text>
        </HStack>
      )}

      {errorMessage && (
        <VStack p={4} gap={2}>
          <HStack gap={2} color="fg.error">
            <AlertCircleIcon size={16} />
            <Text fontSize="sm">{errorMessage}</Text>
          </HStack>
        </VStack>
      )}

      {har && harEntry && !isLoading && (
        <VStack alignItems="stretch" gap={0} width="100%">
          <HStack
            alignItems="center"
            gap={1.5}
            px={3}
            h="36px"
            borderBottomWidth="1px"
            borderColor="border.subtle"
            bg="bg.subtle"
            flexShrink={0}
            width="100%"
            justifyContent="space-between"
            cursor="pointer"
            _hover={{ bg: 'bg.neutral.muted' }}
            onClick={() => setIsExpanded((previous) => !previous)}
          >
            <HStack gap={1.5} flex="1" minW={0}>
              <Box display="flex" alignItems="center" color="fg.muted">
                {isExpanded ? (
                  <ArrowDown01Icon size={10} />
                ) : (
                  <ArrowRight01Icon size={10} />
                )}
              </Box>
              {renderHttpBadge(harEntry.request.method)}
              <Text
                fontSize="xs"
                fontFamily="mono"
                truncate
                maxW="300px"
                title={harEntry.request.url}
              >
                {getUrlPath(harEntry.request.url)}
              </Text>
              {renderHttpBadge(harEntry.response.status)}
              {hopLabel && (
                <Text
                  fontSize="2xs"
                  fontWeight="medium"
                  color="fg.muted"
                  textTransform="uppercase"
                  letterSpacing="wide"
                  flexShrink={0}
                >
                  {hopLabel}
                </Text>
              )}
            </HStack>
            <ToolHarCredentialBadge har={har} />
          </HStack>

          {isExpanded && (
            <Box h="400px">
              <ToolExecutionHarRequestResponseEditors har={har} />
            </Box>
          )}
        </VStack>
      )}
    </VStack>
  );
}
